import { useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, API_URL, formatUSD, formatDate, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, ShieldCheck, AlertCircle, CheckCircle2, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ACCEPTED = ".csv,.xlsx,.xls,.pdf,.png,.jpg,.jpeg,.heic,.heif";

export default function ReconcileTab() {
  const { activeOrgId } = useAuth();
  const fileRef = useRef(null);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState(null);
  const [matchSelections, setMatchSelections] = useState({}); // parsed_index -> boolean

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setData(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("ledger_token") || "";
      const res = await fetch(`${API_URL}/organizations/${activeOrgId}/reconcile/parse`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const result = await res.json();
      setData(result);
      // Pre-check all auto-matched rows
      const initial = {};
      (result.matched || []).forEach((m) => { initial[m.parsed_index] = true; });
      setMatchSelections(initial);
      toast.success(`Matched ${result.matched.length} of ${result.matched.length + result.unmatched_parsed.length} statement lines.`);
    } catch (e) {
      toast.error(`Reconcile parse failed: ${e.message}`);
    } finally {
      setParsing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const apply = async () => {
    if (!data) return;
    const matches = (data.matched || [])
      .filter((m) => matchSelections[m.parsed_index])
      .map((m) => ({ parsed_index: m.parsed_index, ledger_txn_id: m.ledger_txn.id }));
    if (matches.length === 0) return toast.error("Select at least one match to apply.");
    setApplying(true);
    try {
      const { data: result } = await api.post(`/organizations/${activeOrgId}/reconcile/apply`, {
        matches,
        new_transactions: [],
      });
      toast.success(`Reconciled ${result.reconciled} ledger transactions.`);
      setData(null);
      setMatchSelections({});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const toggle = (idx) => setMatchSelections((s) => ({ ...s, [idx]: !s[idx] }));
  const selectedCount = Object.values(matchSelections).filter(Boolean).length;

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm bg-card rounded-3xl p-7">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="font-serif text-2xl">Bank reconciliation</h3>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              Upload a bank statement and ChurchFlow will use Gemini to extract every line, auto-match each
              against your ledger by date + amount, and flag anything that doesn't match. Review, check off the
              matches you trust, then apply — matched ledger transactions are marked <strong>reconciled</strong>.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED}
            onChange={onFile}
            className="hidden"
            data-testid="reconcile-file-input"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={parsing}
            className="rounded-2xl soft-glow h-12 px-6"
            data-testid="reconcile-upload-button"
          >
            <Upload className="w-4 h-4 mr-2" />
            {parsing ? "Reading statement…" : "Upload bank statement"}
          </Button>
          <div className="text-xs text-muted-foreground mt-2">
            Accepted: PDF · PNG / JPG / HEIC photo · CSV · Excel (.xlsx)
          </div>
        </div>
      </Card>

      {parsing && (
        <Card className="border-0 shadow-sm bg-primary/5 rounded-3xl p-6 text-center">
          <Sparkles className="w-8 h-8 text-primary mx-auto animate-pulse" />
          <p className="font-serif text-xl mt-3">Matching against your ledger…</p>
        </Card>
      )}

      {data && (
        <>
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-0 shadow-sm bg-primary/5 rounded-2xl p-5">
              <CheckCircle2 className="w-5 h-5 text-primary" />
              <div className="text-3xl font-serif tabular-nums mt-3">{data.matched.length}</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">Auto-matched</div>
            </Card>
            <Card className="border-0 shadow-sm bg-amber-50 rounded-2xl p-5">
              <AlertCircle className="w-5 h-5 text-amber-700" />
              <div className="text-3xl font-serif tabular-nums mt-3">{data.unmatched_parsed.length}</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">On statement, not in ledger</div>
            </Card>
            <Card className="border-0 shadow-sm bg-secondary rounded-2xl p-5">
              <FileText className="w-5 h-5 text-muted-foreground" />
              <div className="text-3xl font-serif tabular-nums mt-3">{data.ledger_unmatched.length}</div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mt-1">In ledger, not on statement</div>
            </Card>
          </div>

          {data.matched.length > 0 && (
            <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
              <div className="px-6 py-5 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="font-serif text-2xl">Auto-matched lines</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Uncheck anything that looks wrong.</p>
                </div>
                <Button onClick={apply} disabled={applying || selectedCount === 0} className="rounded-2xl soft-glow" data-testid="reconcile-apply-button">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {applying ? "Applying…" : `Mark ${selectedCount} reconciled`}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="reconcile-matches-table">
                  <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-secondary/40">
                    <tr>
                      <th className="px-3 py-2.5 w-10"></th>
                      <th className="px-3 py-2.5 text-left">Statement</th>
                      <th className="px-3 py-2.5 text-left">Ledger</th>
                      <th className="px-3 py-2.5 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.matched.map((m) => (
                      <tr key={m.parsed_index} className="border-b border-border last:border-0">
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={!!matchSelections[m.parsed_index]}
                            onChange={() => toggle(m.parsed_index)}
                            className="w-4 h-4 rounded accent-primary"
                            data-testid={`reconcile-match-${m.parsed_index}`}
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-xs text-muted-foreground tabular-nums">{m.parsed.date}</div>
                          <div className="truncate max-w-xs">{m.parsed.description || m.parsed.raw || "—"}</div>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-xs text-muted-foreground tabular-nums">{formatDate(m.ledger_txn.date)}</div>
                          <div className="truncate max-w-xs">{m.ledger_txn.description || m.ledger_txn.category_name || "—"}</div>
                        </td>
                        <td className={`px-3 py-3 text-right font-mono tabular-nums ${m.parsed.type === "income" ? "text-primary" : "text-expense"}`}>
                          {m.parsed.type === "income" ? "+" : "−"}{formatUSD(m.parsed.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {data.unmatched_parsed.length > 0 && (
            <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
              <div className="px-6 py-5 border-b border-border">
                <h3 className="font-serif text-2xl">On statement, not in ledger</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These lines from the bank weren't found in your ledger. Add them via the <strong>Import</strong> tab if they're legitimate.
                </p>
              </div>
              <div className="divide-y divide-border">
                {data.unmatched_parsed.map((p, idx) => (
                  <div key={idx} className="px-6 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground tabular-nums">{p.date || "—"}</div>
                      <div className="truncate max-w-md">{p.description || p.raw || "—"}</div>
                    </div>
                    <div className={`font-mono tabular-nums shrink-0 ${p.type === "income" ? "text-primary" : "text-expense"}`}>
                      {p.type === "income" ? "+" : "−"}{formatUSD(p.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {data.ledger_unmatched.length > 0 && (
            <Card className="border-0 shadow-sm bg-card rounded-3xl overflow-hidden">
              <div className="px-6 py-5 border-b border-border">
                <h3 className="font-serif text-2xl">In ledger, not on statement</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  These ledger transactions in the same period aren't on your bank statement. Verify they cleared or were recorded in error.
                </p>
              </div>
              <div className="divide-y divide-border">
                {data.ledger_unmatched.map((t) => (
                  <div key={t.id} className="px-6 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground tabular-nums">{formatDate(t.date)}</div>
                      <div className="truncate max-w-md">{t.description || t.category_name || "—"}</div>
                    </div>
                    <div className={`font-mono tabular-nums shrink-0 ${t.type === "income" ? "text-primary" : "text-expense"}`}>
                      {t.type === "income" ? "+" : "−"}{formatUSD(t.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
